import {
  DurationUnit,
  EventType,
  isAjaxErrorUrl,
  isPageRefresh,
  isVisitedUrl,
  Kind,
  NONE,
  UiOnlyKinds,
  UiOnlySubkinds,
} from '@fullstory/search-types';
import {
  ApiErrorClientConverter,
  ClientOnlyConverterSpec,
  ConverterMap,
  FieldEventType,
} from './shared';
import * as helpers from './helpers';
import {
  adjustCriteria,
  apiErrorSearchTypeToFilter,
  elementLikeConverter,
  identity,
  stripPartials,
} from './helpers';
import { DocumentCriterion, SearchOperator as Op } from '@fullstory/client-api';
import * as segment from '@fullstory/proto/fs/api/segment';
import { DefinedEventIdField } from '@fullstory/client-api/goimports/fs_search_doc_fields_special';
import { UID } from '@fullstory/util/uid';

export type EventConverterMap = ConverterMap<EventType> & {
  // Entries for filters that do not have a corresponding client kind
  [segment.EventFilter.filter_keys.custom]: {
    [segment.CustomEventOperator.operator_keys.any]: ClientOnlyConverterSpec;
    [segment.CustomEventOperator.operator_keys.named]: ClientOnlyConverterSpec;
  };
  [segment.EventFilter.filter_keys.definedEvent]: {
    [segment.DefinedEventOperator.operator_keys.withId]: ClientOnlyConverterSpec;
  };
  [segment.EventFilter.filter_keys.specEvent]: {
    [segment.SpecEventOperator.operator_keys.specId]: ClientOnlyConverterSpec;
  };
  [segment.EventFilter.filter_keys.customError]: ApiErrorClientConverter;
  [segment.EventFilter.filter_keys.userError]: ApiErrorClientConverter;
  [segment.EventFilter.filter_keys.pageError]: ApiErrorClientConverter;
  [segment.EventFilter.filter_keys.seenError]: ApiErrorClientConverter;
  [segment.EventFilter.filter_keys.elemError]: ApiErrorClientConverter;
};

export type DefaultPartialEventMap = {
  [kind in Kind<EventType>]?: {
    defaultType: () => EventType;
  };
};

// partial rows should filter to the default partial EventType
export const DefaultPartialEventMap: DefaultPartialEventMap = {
  [segment.EventFilter.filter_keys.click]: {
    defaultType: () => ({
      kind: segment.EventFilter.filter_keys.click,
      subkind: segment.ElementOperator.operator_keys.any,
      value: NONE,
    }),
  },
  [segment.EventFilter.filter_keys.change]: {
    defaultType: () => ({
      kind: segment.EventFilter.filter_keys.change,
      subkind: segment.ElementOperator.operator_keys.any,
      value: NONE,
    }),
  },
  [segment.EventFilter.filter_keys.errorClick]: {
    defaultType: () => ({
      kind: segment.EventFilter.filter_keys.errorClick,
      subkind: segment.ElementOperator.operator_keys.any,
      value: NONE,
    }),
  },
  [segment.EventFilter.filter_keys.rageClick]: {
    defaultType: () => ({
      kind: segment.EventFilter.filter_keys.rageClick,
      subkind: segment.ElementOperator.operator_keys.any,
      value: NONE,
    }),
  },
  [segment.EventFilter.filter_keys.deadClick]: {
    defaultType: () => ({
      kind: segment.EventFilter.filter_keys.deadClick,
      subkind: segment.ElementOperator.operator_keys.any,
      value: NONE,
    }),
  },
  [segment.EventFilter.filter_keys.seen]: {
    defaultType: () => ({
      kind: segment.EventFilter.filter_keys.seen,
      subkind: segment.ElementOperator.operator_keys.any,
      value: NONE,
    }),
  },
  [segment.EventFilter.filter_keys.suspiciousActivity]: {
    defaultType: () => ({
      kind: segment.EventFilter.filter_keys.suspiciousActivity,
      subkind: segment.ElementOperator.operator_keys.any,
      value: NONE,
    }),
  },
  [segment.EventFilter.filter_keys.abandonedForm]: {
    defaultType: () => ({
      kind: segment.EventFilter.filter_keys.abandonedForm,
      subkind: segment.AbandonedOperator.operator_keys.any,
      value: NONE,
    }),
  },
  [UiOnlyKinds.apiError]: {
    defaultType: () => ({
      kind: UiOnlyKinds.apiError,
      subkind: segment.EventFilter.filter_keys.customError,
      value: segment.ApiErrorOperator.operator_keys.any,
    }),
  },
  [segment.EventFilter.filter_keys.visitedUrl]: {
    defaultType: () => ({
      kind: segment.EventFilter.filter_keys.visitedUrl,
      subkind: segment.UrlOperator.operator_keys.any,
      value: NONE,
    }),
  },
  [segment.EventFilter.filter_keys.pageRefresh]: {
    defaultType: () => ({
      kind: segment.EventFilter.filter_keys.pageRefresh,
      subkind: segment.UrlOperator.operator_keys.any,
      value: NONE,
    }),
  },
  [segment.EventFilter.filter_keys.ajaxErrorUrl]: {
    defaultType: () => ({
      kind: segment.EventFilter.filter_keys.ajaxErrorUrl,
      subkind: segment.UrlOperator.operator_keys.any,
      value: NONE,
    }),
  },
  [segment.EventFilter.filter_keys.highlight]: {
    defaultType: () => ({
      kind: segment.EventFilter.filter_keys.highlight,
      subkind: segment.ElementOperator.operator_keys.any,
      value: NONE,
    }),
  },
  [segment.EventFilter.filter_keys.copy]: {
    defaultType: () => ({
      kind: segment.EventFilter.filter_keys.copy,
      subkind: segment.ElementOperator.operator_keys.any,
      value: NONE,
    }),
  },
  [segment.EventFilter.filter_keys.paste]: {
    defaultType: () => ({
      kind: segment.EventFilter.filter_keys.paste,
      subkind: segment.ElementOperator.operator_keys.any,
      value: NONE,
    }),
  },
  [segment.EventFilter.filter_keys.uncaughtException]: {
    defaultType: () => ({
      kind: segment.EventFilter.filter_keys.uncaughtException,
      subkind: segment.StringOperator.operator_keys.any,
      value: NONE,
    }),
  },
  [segment.EventFilter.filter_keys.navigatedToScreen]: {
    defaultType: () => ({
      kind: segment.EventFilter.filter_keys.navigatedToScreen,
      subkind: segment.StringOperator.operator_keys.any,
      value: NONE,
    }),
  },
  [segment.EventFilter.filter_keys.visitedPage]: {
    defaultType: () => ({
      kind: segment.EventFilter.filter_keys.visitedPage,
      subkind: segment.PageRefOperator.operator_keys.any,
      value: NONE,
    }),
  },
  [segment.EventFilter.filter_keys.consoleError]: {
    defaultType: () => ({
      kind: segment.EventFilter.filter_keys.consoleError,
      subkind: segment.StringOperator.operator_keys.any,
      value: NONE,
    }),
  },
};

const visitedUrl = helpers.urlConverter<EventType, segment.EventFilter.filter_keys.visitedUrl>(
  segment.EventFilter.filter_keys.visitedUrl,
  'PageUrl',
  1,
  s => isVisitedUrl(s),
  helpers.visitedUrlCategoryToField,
  helpers.fieldToVisitedUrlCategory,
  helpers.searchTypeToFilterUrl,
  helpers.filterToSearchTypeEventVisitedUrl,
  adjustCriteria((c: DocumentCriterion[]): DocumentCriterion[] => [
    { Field: FieldEventType, Op: Op.SEARCH_OP_EQ, Value: 'navigate' },
    ...c,
  ]),
);

const pageRefresh = helpers.urlConverter<EventType, segment.EventFilter.filter_keys.pageRefresh>(
  segment.EventFilter.filter_keys.pageRefresh,
  'PageUrl',
  1,
  s => isPageRefresh(s),
  helpers.pageRefreshUrlCategoryToField,
  helpers.fieldToPageRefreshUrlCategory,
  helpers.searchTypeToFilterUrl,
  helpers.filterToSearchTypeEventPageRefresh,
  adjustCriteria((c: DocumentCriterion[]): DocumentCriterion[] => [
    { Field: FieldEventType, Op: Op.SEARCH_OP_EQ, Value: 'refresh' },
    ...c,
  ]),
);

const ajaxErrorUrl = helpers.urlConverter<EventType, segment.EventFilter.filter_keys.ajaxErrorUrl>(
  segment.EventFilter.filter_keys.ajaxErrorUrl,
  'ReqUrl',
  1,
  s => isAjaxErrorUrl(s),
  helpers.ajaxUrlCategoryToField,
  helpers.fieldToAjaxUrlCategory,
  helpers.searchTypeToFilterUrl,
  helpers.filterToSearchTypeAjaxVisitedUrl,
  adjustCriteria((c: DocumentCriterion[]): DocumentCriterion[] => [
    { Field: FieldEventType, Op: Op.SEARCH_OP_EQ, Value: 'request' },
    ...c,
  ]),
);

const navigatedToScreen = helpers.legacyStringOperatorConverter<
  EventType,
  segment.EventFilter.filter_keys.navigatedToScreen
>(
  segment.EventFilter.filter_keys.navigatedToScreen,
  'AppViewName',
  1,
  undefined,
  true,
  adjustCriteria((c: DocumentCriterion[]): DocumentCriterion[] => [
    { Field: FieldEventType, Op: Op.SEARCH_OP_EQ, Value: 'navigate' },
    ...c,
  ]),
);

const uncaughtException = helpers.legacyStringOperatorConverter<
  EventType,
  segment.EventFilter.filter_keys.uncaughtException
>(
  segment.EventFilter.filter_keys.uncaughtException,
  'EventTargetText',
  1,
  undefined,
  true,
  adjustCriteria((c: DocumentCriterion[]): DocumentCriterion[] => [
    { Field: FieldEventType, Op: Op.SEARCH_OP_EQ, Value: 'exception' },
    ...c,
  ]),
);

const consoleError = helpers.stringOperatorConverter<
  EventType,
  segment.EventFilter.filter_keys.consoleError
>();

const pageLoad = helpers.durationOperatorConverter<
  EventType,
  segment.EventFilter.filter_keys.pageLoad
>(
  segment.EventFilter.filter_keys.pageLoad,
  'LoadEventTime',
  1,
  DurationUnit.milli,
  adjustCriteria((c: DocumentCriterion[]): DocumentCriterion[] => [
    { Field: FieldEventType, Op: Op.SEARCH_OP_EQ, Value: 'load' },
    ...c,
  ]),
);

const cumulativeLayoutShift = helpers.floatOperatorConverter<
  EventType,
  segment.EventFilter.filter_keys.cumulativeLayoutShift
>(segment.EventFilter.filter_keys.cumulativeLayoutShift, 'EventCumulativeLayoutShift');

const domContentLoaded = helpers.durationOperatorConverter<
  EventType,
  segment.EventFilter.filter_keys.domContentLoaded
>(
  segment.EventFilter.filter_keys.domContentLoaded,
  'LoadDomContentTime',
  1,
  DurationUnit.milli,
  adjustCriteria(c => [{ Field: FieldEventType, Op: Op.SEARCH_OP_EQ, Value: 'load' }, ...c]),
);

const firstMeaningfulPaint = helpers.durationOperatorConverter<
  EventType,
  segment.EventFilter.filter_keys.firstMeaningfulPaint
>(
  segment.EventFilter.filter_keys.firstMeaningfulPaint,
  'LoadFirstPaintTime',
  1,
  DurationUnit.milli,
  adjustCriteria((c: DocumentCriterion[]): DocumentCriterion[] => [
    { Field: FieldEventType, Op: Op.SEARCH_OP_EQ, Value: 'load' },
    ...c,
  ]),
);

const largestContentfulPaint = helpers.durationOperatorConverter<
  EventType,
  segment.EventFilter.filter_keys.largestContentfulPaint
>(
  segment.EventFilter.filter_keys.largestContentfulPaint,
  'LoadLargestPaintTime',
  1,
  DurationUnit.milli,
  adjustCriteria((c: DocumentCriterion[]): DocumentCriterion[] => [
    { Field: FieldEventType, Op: Op.SEARCH_OP_EQ, Value: 'load' },
    ...c,
  ]),
);

const firstInputDelay = helpers.durationOperatorConverter<
  EventType,
  segment.EventFilter.filter_keys.firstInputDelay
>(
  segment.EventFilter.filter_keys.firstInputDelay,
  'EventFirstInputDelay',
  1,
  DurationUnit.milli,
  adjustCriteria((c: DocumentCriterion[]): DocumentCriterion[] => [
    { Field: FieldEventType, Op: Op.SEARCH_OP_EQ, Value: 'first_input_delay' },
    ...c,
  ]),
);

const interactionToNextPaint = helpers.durationOperatorConverter<
  EventType,
  segment.EventFilter.filter_keys.interactionToNextPaint
>(
  segment.EventFilter.filter_keys.interactionToNextPaint,
  'EventInteractionToNextPaint',
  1,
  DurationUnit.milli,
  adjustCriteria((c: DocumentCriterion[]): DocumentCriterion[] => [
    { Field: FieldEventType, Op: Op.SEARCH_OP_EQ, Value: 'interaction_to_next_paint' },
    ...c,
  ]),
);

const timeToInteractive = helpers.durationOperatorConverter<
  EventType,
  segment.EventFilter.filter_keys.timeToInteractive
>(
  segment.EventFilter.filter_keys.timeToInteractive,
  'EventTimeToInteractive',
  1,
  DurationUnit.milli,
  adjustCriteria((c: DocumentCriterion[]): DocumentCriterion[] => [
    { Field: FieldEventType, Op: Op.SEARCH_OP_EQ, Value: 'time_to_interactive' },
    ...c,
  ]),
);

const totalBlockingTime = helpers.durationOperatorConverter<
  EventType,
  segment.EventFilter.filter_keys.totalBlockingTime
>(
  segment.EventFilter.filter_keys.totalBlockingTime,
  'EventTotalBlockingTime',
  1,
  DurationUnit.milli,
  adjustCriteria((c: DocumentCriterion[]): DocumentCriterion[] => [
    { Field: FieldEventType, Op: Op.SEARCH_OP_EQ, Value: 'total_blocking_time' },
    ...c,
  ]),
);

const timeToFirstByte = helpers.durationOperatorConverter<
  EventType,
  segment.EventFilter.filter_keys.timeToFirstByte
>(
  segment.EventFilter.filter_keys.timeToFirstByte,
  'EventTimeToFirstByte',
  1,
  DurationUnit.milli,
  adjustCriteria((c: DocumentCriterion[]): DocumentCriterion[] => [
    { Field: FieldEventType, Op: Op.SEARCH_OP_EQ, Value: 'time_to_first_byte' },
    ...c,
  ]),
);

const customError = helpers.apiErrorConverter(segment.EventFilter.filter_keys.customError);
const userError = helpers.apiErrorConverter(segment.EventFilter.filter_keys.userError);
const pageError = helpers.apiErrorConverter(segment.EventFilter.filter_keys.pageError);
const elemError = helpers.apiErrorConverter(segment.EventFilter.filter_keys.elemError);
const seenError = helpers.apiErrorConverter(segment.EventFilter.filter_keys.seenError);

const visitedPage = helpers.pageIdConverter<
  EventType,
  segment.EventFilter.filter_keys.visitedPage
>();

const dataloss = helpers.datalossConverter<EventType, segment.EventFilter.filter_keys.dataloss>();

// EventConverterMap defines the specification to convert between the UI's
// representation of events and the server's representation.
export const EventConverterMap: EventConverterMap = {
  [NONE]: {
    [NONE]: {
      criteria: () => [],
      withoutPartials: () => null,
      extractFromCriteria: () => null,
      extractFromPartialCriteria: () => null,
      searchTypeToFilter: () => null,
      filterToSearchType: () => null,
    },
  },
  any: {
    [NONE]: {
      criteria: () => [],
      withoutPartials: s => helpers.stripPartials(s, helpers.identity),
      extractFromCriteria: c => ({
        kind: 'any',
        subkind: NONE,
        value: NONE,
      }),
      extractFromPartialCriteria: () => null,
      searchTypeToFilter: st => null,
      filterToSearchType: sf => ({
        kind: 'any',
        subkind: NONE,
        value: NONE,
      }),
    },
  },

  [segment.EventFilter.filter_keys.click]: elementLikeConverter(
    segment.EventFilter.filter_keys.click,
    { eventType: 'click' },
  ),
  [segment.EventFilter.filter_keys.change]: elementLikeConverter(
    segment.EventFilter.filter_keys.change,
    { eventType: 'change' },
  ),
  [segment.EventFilter.filter_keys.rageClick]: elementLikeConverter(
    segment.EventFilter.filter_keys.rageClick,
    { eventType: 'click', modField: 'EventModFrustrated' },
  ),
  [segment.EventFilter.filter_keys.errorClick]: elementLikeConverter(
    segment.EventFilter.filter_keys.errorClick,
    { eventType: 'click', modField: 'EventModError' },
  ),
  [segment.EventFilter.filter_keys.deadClick]: elementLikeConverter(
    segment.EventFilter.filter_keys.deadClick,
    { eventType: 'click', modField: 'EventModDead' },
  ),
  [segment.EventFilter.filter_keys.visitedUrl]: visitedUrl,
  [segment.EventFilter.filter_keys.pageRefresh]: pageRefresh,
  [segment.EventFilter.filter_keys.ajaxErrorUrl]: ajaxErrorUrl,
  [segment.EventFilter.filter_keys.seen]: elementLikeConverter(
    segment.EventFilter.filter_keys.seen,
    { eventType: 'seen' },
  ),
  [segment.EventFilter.filter_keys.suspiciousActivity]: elementLikeConverter(
    segment.EventFilter.filter_keys.suspiciousActivity,
    { modField: 'EventModSuspicious' },
  ),
  [segment.EventFilter.filter_keys.navigatedToScreen]: navigatedToScreen,
  [segment.EventFilter.filter_keys.pageLoad]: pageLoad,
  [segment.EventFilter.filter_keys.thrashedCursor]: {
    [NONE]: {
      criteria: () => [{ Field: FieldEventType, Op: Op.SEARCH_OP_EQ, Value: 'thrash' }],
      withoutPartials: s => helpers.stripPartials(s, helpers.identity),
      extractFromCriteria: c => ({
        kind: segment.EventFilter.filter_keys.thrashedCursor,
        subkind: NONE,
        value: NONE,
      }),
      extractFromPartialCriteria: c =>
        c[0] &&
        c[0].Field == FieldEventType &&
        c[0].Value == 'thrash' && {
          kind: segment.EventFilter.filter_keys.thrashedCursor,
          subkind: NONE,
          value: NONE,
        },
      searchTypeToFilter: () => ({
        thrashedCursor: {},
      }),
      filterToSearchType: () => ({
        kind: segment.EventFilter.filter_keys.thrashedCursor,
        subkind: NONE,
        value: NONE,
      }),
    },
  },
  [segment.EventFilter.filter_keys.cumulativeLayoutShift]: cumulativeLayoutShift,
  [segment.EventFilter.filter_keys.domContentLoaded]: domContentLoaded,
  [segment.EventFilter.filter_keys.crashed]: {
    [NONE]: {
      criteria: s => [{ Field: FieldEventType, Op: Op.SEARCH_OP_EQ, Value: 'crashed' }],
      withoutPartials: s => helpers.stripPartials(s, helpers.identity),
      extractFromCriteria: c => ({
        kind: segment.EventFilter.filter_keys.crashed,
        subkind: NONE,
        value: NONE,
      }),
      extractFromPartialCriteria: c =>
        c.length == 1 &&
        c[0].Field == FieldEventType &&
        c[0].Value == 'crashed' && {
          kind: segment.EventFilter.filter_keys.crashed,
          subkind: NONE,
          value: NONE,
        },
      searchTypeToFilter: () => ({
        crashed: {},
      }),
      filterToSearchType: () => ({
        kind: segment.EventFilter.filter_keys.crashed,
        subkind: NONE,
        value: NONE,
      }),
    },
  },
  [segment.EventFilter.filter_keys.lowMemory]: {
    [NONE]: {
      criteria: s => [{ Field: FieldEventType, Op: Op.SEARCH_OP_EQ, Value: 'low_memory' }],
      withoutPartials: s => helpers.stripPartials(s, helpers.identity),
      extractFromCriteria: c => ({
        kind: segment.EventFilter.filter_keys.lowMemory,
        subkind: NONE,
        value: NONE,
      }),
      extractFromPartialCriteria: c =>
        c.length == 1 &&
        c[0].Field == FieldEventType &&
        c[0].Value == 'low_memory' && {
          kind: segment.EventFilter.filter_keys.lowMemory,
          subkind: NONE,
          value: NONE,
        },
      searchTypeToFilter: () => ({
        lowMemory: {},
      }),
      filterToSearchType: () => ({
        kind: segment.EventFilter.filter_keys.lowMemory,
        subkind: NONE,
        value: NONE,
      }),
    },
  },
  [segment.EventFilter.filter_keys.abandonedForm]: elementLikeConverter(
    segment.EventFilter.filter_keys.abandonedForm,
    {
      eventType: 'abandon',
      includes: [
        segment.ElementOperator.operator_keys.any,
        segment.ElementOperator.operator_keys.css,
        segment.ElementOperator.operator_keys.cssIsNot,
        segment.ElementOperator.operator_keys.withElementId,
      ],
    },
  ),
  [segment.EventFilter.filter_keys.firstMeaningfulPaint]: firstMeaningfulPaint,
  [segment.EventFilter.filter_keys.largestContentfulPaint]: largestContentfulPaint,
  [segment.EventFilter.filter_keys.firstInputDelay]: firstInputDelay,
  [segment.EventFilter.filter_keys.interactionToNextPaint]: interactionToNextPaint,
  [segment.EventFilter.filter_keys.timeToInteractive]: timeToInteractive,
  [segment.EventFilter.filter_keys.totalBlockingTime]: totalBlockingTime,
  [segment.EventFilter.filter_keys.timeToFirstByte]: timeToFirstByte,
  [segment.EventFilter.filter_keys.uncaughtException]: uncaughtException,
  [segment.EventFilter.filter_keys.consoleError]: consoleError,
  [segment.EventFilter.filter_keys.highlight]: elementLikeConverter(
    segment.EventFilter.filter_keys.highlight,
    { eventType: 'highlight' },
  ),
  [segment.EventFilter.filter_keys.copy]: elementLikeConverter(
    segment.EventFilter.filter_keys.copy,
    { eventType: 'copy' },
  ),
  [segment.EventFilter.filter_keys.paste]: elementLikeConverter(
    segment.EventFilter.filter_keys.paste,
    { eventType: 'paste' },
  ),
  [UiOnlyKinds.orgEvent]: {
    [UiOnlySubkinds.customAny]: {
      criteria: s => [{ Field: FieldEventType, Op: Op.SEARCH_OP_EQ, Value: 'custom' }],
      withoutPartials: s => helpers.stripPartials(s, helpers.identity),
      extractFromCriteria: c => ({
        kind: UiOnlyKinds.orgEvent,
        subkind: UiOnlySubkinds.customAny,
        value: NONE,
      }),
      extractFromPartialCriteria: c =>
        c.length == 1 &&
        c[0].Value == 'custom' && {
          kind: UiOnlyKinds.orgEvent,
          subkind: UiOnlySubkinds.customAny,
          value: NONE,
        },
      searchTypeToFilter: st => ({
        custom: {
          any: {},
        },
      }),
      filterToSearchType: () => null, // UI-only
    },
    [UiOnlySubkinds.customNamed]: {
      criteria: s => [
        { Field: FieldEventType, Op: Op.SEARCH_OP_EQ, Value: 'custom' },
        { Field: 'EventCustomName', Op: Op.SEARCH_OP_EQ, Value: s.value.name },
      ],
      withoutPartials: s => helpers.stripPartials(s, helpers.identity),
      extractFromCriteria: c => {
        if (c[1] == null) {
          return null;
        }
        return {
          kind: UiOnlyKinds.orgEvent,
          subkind: UiOnlySubkinds.customNamed,
          value: {
            name: c[1].Value as string,
          },
        };
      },
      extractFromPartialCriteria: c =>
        c[0].Field == FieldEventType &&
        c[0].Value == 'custom' &&
        c[1].Field == 'EventCustomName' && {
          kind: UiOnlyKinds.orgEvent,
          subkind: UiOnlySubkinds.customNamed,
          value: {
            name: c[1].Value as string,
          },
        },
      searchTypeToFilter: st => ({
        custom: {
          named: {
            name: st.value.name,
          },
        },
      }),
      filterToSearchType: () => null, // UI-only
    },
    // Placeholder type
    [UiOnlySubkinds.definedDefault]: {
      criteria: s => [],
      withoutPartials: () => null,
      extractFromCriteria: () => null,
      extractFromPartialCriteria: () => null,
      searchTypeToFilter: () => null,
      filterToSearchType: () => null,
    },
    [UiOnlySubkinds.definedNamed]: {
      criteria: s => [{ Field: DefinedEventIdField, Op: Op.SEARCH_OP_EQ, Value: s.value.id }],
      withoutPartials: s => helpers.stripPartials(s, helpers.identity),
      extractFromCriteria: c => ({
        kind: UiOnlyKinds.orgEvent,
        subkind: UiOnlySubkinds.definedNamed,
        value: {
          id: c[0].Value as string,
          name: '',
        },
      }),
      extractFromPartialCriteria: c =>
        c[0].Field == DefinedEventIdField && {
          kind: UiOnlyKinds.orgEvent,
          subkind: UiOnlySubkinds.definedNamed,
          value: {
            id: c[0].Value as string,
            name: '',
          },
        },
      searchTypeToFilter: st => ({
        definedEvent: {
          withId: {
            value: st.value.id,
          },
        },
      }),
      filterToSearchType: () => null, // UI-only
    },
    [UiOnlySubkinds.specObject]: {
      criteria: s => null,
      withoutPartials: s => helpers.stripPartials(s, helpers.identity),
      extractFromCriteria: c => null,
      extractFromPartialCriteria: c => null,
      searchTypeToFilter: st => ({
        specEvent: {
          specId: {
            value: st.value.slug,
          },
        },
      }),
      filterToSearchType: () => null, // UI-only
    },
  },
  // Converts "custom" filters to client only "orgEvent" SearchType
  // "orgEvent" combines custom events and defined events into a single client type
  [segment.EventFilter.filter_keys.custom]: {
    [segment.CustomEventOperator.operator_keys.any]: {
      filterToSearchType: sf => ({
        kind: UiOnlyKinds.orgEvent,
        subkind: UiOnlySubkinds.customAny,
        value: NONE,
      }),
    },
    [segment.CustomEventOperator.operator_keys.named]: {
      filterToSearchType: sf => ({
        kind: UiOnlyKinds.orgEvent,
        subkind: UiOnlySubkinds.customNamed,
        value: {
          name: sf.custom.named.name,
        },
      }),
    },
  },
  [segment.EventFilter.filter_keys.definedEvent]: {
    [segment.DefinedEventOperator.operator_keys.withId]: {
      filterToSearchType: sf => ({
        kind: UiOnlyKinds.orgEvent,
        subkind: UiOnlySubkinds.definedNamed,
        value: {
          id: sf.definedEvent.withId.value,
          name: '',
        },
      }),
    },
  },
  [segment.EventFilter.filter_keys.specEvent]: {
    [segment.SpecEventOperator.operator_keys.specId]: {
      filterToSearchType: sf => ({
        kind: UiOnlyKinds.orgEvent,
        subkind: UiOnlySubkinds.specObject,
        value: {
          slug: sf.specEvent.specId.value,
        },
      }),
    },
  },
  [UiOnlyKinds.apiError]: {
    [segment.EventFilter.filter_keys.customError]: {
      filterToSearchType: () => null, // UI-only
      withoutPartials: s => stripPartials(s, identity),
      searchTypeToFilter: st => apiErrorSearchTypeToFilter(st),
    },
    [segment.EventFilter.filter_keys.userError]: {
      filterToSearchType: () => null, // UI-only
      withoutPartials: s => stripPartials(s, identity),
      searchTypeToFilter: st => apiErrorSearchTypeToFilter(st),
    },
    [segment.EventFilter.filter_keys.pageError]: {
      filterToSearchType: () => null, // UI-only
      withoutPartials: s => stripPartials(s, identity),
      searchTypeToFilter: st => apiErrorSearchTypeToFilter(st),
    },
    [segment.EventFilter.filter_keys.elemError]: {
      filterToSearchType: () => null, // UI-only
      withoutPartials: s => stripPartials(s, identity),
      searchTypeToFilter: st => apiErrorSearchTypeToFilter(st),
    },
    [segment.EventFilter.filter_keys.seenError]: {
      filterToSearchType: () => null, // UI-only
      withoutPartials: s => stripPartials(s, identity),
      searchTypeToFilter: st => apiErrorSearchTypeToFilter(st),
    },
  },
  [segment.EventFilter.filter_keys.customError]: customError,
  [segment.EventFilter.filter_keys.userError]: userError,
  [segment.EventFilter.filter_keys.pageError]: pageError,
  [segment.EventFilter.filter_keys.seenError]: seenError,
  [segment.EventFilter.filter_keys.elemError]: elemError,
  [segment.EventFilter.filter_keys.pinchGesture]: {
    [NONE]: {
      searchTypeToFilter: () => ({
        pinchGesture: {},
      }),
      filterToSearchType: () => ({
        kind: segment.EventFilter.filter_keys.pinchGesture,
        subkind: NONE,
        value: NONE,
      }),
      withoutPartials: s => helpers.stripPartials(s, helpers.identity),
      criteria: s => [],
      extractFromCriteria: c => null,
      extractFromPartialCriteria: c => null,
    },
  },
  [segment.EventFilter.filter_keys.dataloss]: dataloss,
  [segment.EventFilter.filter_keys.eventSet]: {
    [segment.EventSetOperator.operator_keys.union]: {
      searchTypeToFilter: (st, eventToFilter, dependentToFilter) => ({
        eventSet: {
          union: {
            events: st.value
              .filter(v => v)
              .map(v => ({
                primary: eventToFilter(v.primary),
                deps: v.dependents.map(dependentToFilter),
              })),
          },
        },
      }),
      filterToSearchType: (sf, filterToEvent, filterToDependent) => ({
        kind: segment.EventFilter.filter_keys.eventSet,
        subkind: segment.EventSetOperator.operator_keys.union,
        value: sf.eventSet.union.events.map(e => ({
          primary: {
            id: UID(),
            ...filterToEvent(e.primary),
          },
          dependents: e.deps.map(d => ({
            id: UID(),
            ...filterToDependent(d),
          })),
        })),
      }),
      withoutPartials: (st, withoutPartials) =>
        helpers.stripPartials(st, egs => {
          const egsNoPartials = egs
            .map(withoutPartials)
            .filter(
              noPartials =>
                noPartials && (noPartials.primary.kind != 'any' || !!noPartials.dependents.length),
            );
          return {
            ok: !!egsNoPartials.length,
            value: egsNoPartials,
          };
        }),
      criteria: st => [],
      extractFromCriteria: c => null,
      extractFromPartialCriteria: c => null,
    },
  },
  [segment.EventFilter.filter_keys.visitedPage]: visitedPage,
  [segment.EventFilter.filter_keys.keepSignal]: {
    [NONE]: {
      criteria: () => [{ Field: FieldEventType, Op: Op.SEARCH_OP_EQ, Value: 'focus' }],
      withoutPartials: s => helpers.stripPartials(s, helpers.identity),
      extractFromCriteria: c => ({
        kind: segment.EventFilter.filter_keys.keepSignal,
        subkind: NONE,
        value: NONE,
      }),
      extractFromPartialCriteria: c =>
        c[0] &&
        c[0].Field == FieldEventType &&
        c[0].Value == 'focus' && {
          kind: segment.EventFilter.filter_keys.keepSignal,
          subkind: NONE,
          value: NONE,
        },
      searchTypeToFilter: () => ({
        keepSignal: {},
      }),
      filterToSearchType: () => ({
        kind: segment.EventFilter.filter_keys.keepSignal,
        subkind: NONE,
        value: NONE,
      }),
    },
  },
  [segment.EventFilter.filter_keys.appForcedRestart]: {
    [NONE]: {
      criteria: s => [{ Field: FieldEventType, Op: Op.SEARCH_OP_EQ, Value: 'app_forced_restart' }],
      withoutPartials: s => helpers.stripPartials(s, helpers.identity),
      extractFromCriteria: c => ({
        kind: segment.EventFilter.filter_keys.appForcedRestart,
        subkind: NONE,
        value: NONE,
      }),
      extractFromPartialCriteria: c =>
        c.length == 1 &&
        c[0].Field == FieldEventType &&
        c[0].Value == 'app_forced_restart' && {
          kind: segment.EventFilter.filter_keys.appForcedRestart,
          subkind: NONE,
          value: NONE,
        },
      searchTypeToFilter: () => ({
        appForcedRestart: {},
      }),
      filterToSearchType: () => ({
        kind: segment.EventFilter.filter_keys.appForcedRestart,
        subkind: NONE,
        value: NONE,
      }),
    },
  },
  [segment.EventFilter.filter_keys.backgrounded]: {
    [NONE]: {
      criteria: s => [{ Field: FieldEventType, Op: Op.SEARCH_OP_EQ, Value: 'backgrounded' }],
      withoutPartials: s => helpers.stripPartials(s, helpers.identity),
      extractFromCriteria: c => ({
        kind: segment.EventFilter.filter_keys.backgrounded,
        subkind: NONE,
        value: NONE,
      }),
      extractFromPartialCriteria: c =>
        c.length == 1 &&
        c[0].Field == FieldEventType &&
        c[0].Value == 'backgrounded' && {
          kind: segment.EventFilter.filter_keys.backgrounded,
          subkind: NONE,
          value: NONE,
        },
      searchTypeToFilter: () => ({
        backgrounded: {},
      }),
      filterToSearchType: () => ({
        kind: segment.EventFilter.filter_keys.backgrounded,
        subkind: NONE,
        value: NONE,
      }),
    },
  },
};
